import React from "react";
import { Layout } from "@/components/ui/layout";

// NavBar skeleton that matches the NavBar component
const NavBarSkeleton = () => (
  <header className="fixed top-0 left-0 right-0 bg-base-background/55 backdrop-blur-md z-50 border-b border-base-border-light" role="banner" aria-label="Loading navigation">
    <div className="mx-auto px-4 md:px-12 max-w-screen-xl">
      <div className="flex items-center justify-between py-4 animate-pulse">
        {/* Logo placeholder */}
        <div className="h-8 w-24 bg-gray-200 rounded" aria-hidden="true"></div>
        
        {/* Navigation Tabs placeholder - Desktop */}
        <nav className="hidden md:block absolute left-1/2 transform -translate-x-1/2" aria-label="Main navigation placeholder">
          <div className="h-10 w-96 bg-gray-200 rounded-full" aria-hidden="true"></div>
        </nav>

        {/* Auth Buttons placeholder - Desktop */}
        <div className="hidden md:flex items-center gap-4" aria-hidden="true">
          <div className="h-10 w-20 bg-gray-200 rounded-md"></div>
          <div className="h-10 w-20 bg-gray-200 rounded-md"></div>
        </div>
        
        {/* Mobile Menu Button placeholder */}
        <div className="md:hidden h-10 w-10 bg-gray-200 rounded-md" aria-hidden="true"></div>
      </div>
    </div>
  </header>
);

// Hero section skeleton that matches the first section
const HeroSectionSkeleton = () => (
  <section className="min-h-screen flex items-center" aria-label="Loading hero section">
    <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-8 items-center animate-pulse">
      {/* Text content column */}
      <div className="space-y-8 max-w-xl">
        {/* Large heading placeholder - matches the large hero text */}
        <div className="space-y-3" aria-hidden="true">
          <div className="h-10 bg-gray-200 rounded w-5/6"></div>
          <div className="h-10 bg-gray-200 rounded w-full"></div>
          <div className="h-10 bg-gray-200 rounded w-4/5"></div>
          <div className="h-10 bg-gray-200 rounded w-3/4"></div>
        </div>
        
        {/* Button placeholder */}
        <div className="h-12 bg-gray-200 rounded-full w-40" aria-hidden="true"></div>
      </div>
      
      {/* Image placeholder - matches the hero image */}
      <div className="h-96 bg-gray-200 rounded-lg w-full" aria-hidden="true" role="img" aria-label="Loading image"></div>
    </div>
  </section>
);

// Regular section skeleton for other sections
const SectionSkeleton = () => (
  <section className="py-24 md:py-32" aria-label="Loading content section">
    <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-12 items-center animate-pulse">
      {/* Text content column */}
      <div className="space-y-6">
        {/* Badge placeholder */}
        <div className="flex items-center" aria-hidden="true">
          <div className="h-7 w-28 bg-gray-200 rounded-full"></div>
        </div>
        
        {/* Heading placeholder */}
        <div className="space-y-2" aria-hidden="true">
          <div className="h-9 bg-gray-200 rounded w-4/5"></div>
          <div className="h-9 bg-gray-200 rounded w-3/4"></div>
        </div>
        
        {/* Body text placeholder */}
        <div className="space-y-2 mt-4" aria-hidden="true">
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          <div className="h-4 bg-gray-200 rounded w-4/5"></div>
        </div>
        
        {/* Button placeholder */}
        <div className="h-11 bg-gray-200 rounded-full w-36 mt-4" aria-hidden="true"></div>
      </div>
      
      {/* Image placeholder */}
      <div className="h-80 md:h-96 bg-gray-200 rounded-lg w-full" aria-hidden="true" role="img" aria-label="Loading image"></div>
    </div>
  </section>
);

// Main app skeleton that combines all skeletons
const AppSkeleton: React.FC = () => {
  return (
    <div 
      className="bg-base-background text-base-foreground mx-auto min-h-dvh max-w-screen-2xl scroll-smooth"
      role="status"
      aria-label="Loading content"
      aria-live="polite"
    >
      <div className="sr-only">Page content is loading. Please wait.</div>
      <NavBarSkeleton />
      <main>
        <Layout>
          <HeroSectionSkeleton />
          <SectionSkeleton />
          <SectionSkeleton />
          <SectionSkeleton />
        </Layout>
      </main>
    </div>
  );
};

export default AppSkeleton; 